import React from "react";
import {
  CSVIcon,
  PDFIcon,
  PDFCancelledIcon,
  CSVCancelledIcon,
  DeleteReport,
} from "../../../images";
import {
  Card,
  CardContent,
  LinearProgress,
  Box,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import Text from "../text/text";
import Image from "../image/image";
import classNames from "classnames";
import styles from "./download-card.module.scss";
import Pill from "../pill/pill";
import { dateFormat, prettifyReportType, formatBytes } from "utils";
import Button from "../button/button";
import locale from "localization";
import { ReportStatus } from "enums";
import { ClearOutlined, DeleteOutlineOutlined } from "@material-ui/icons";
import { useContext } from "react";
import { useApi, useAlert, useModal } from "hooks";
import { DownloadsContext } from "contexts";
import { SuccessModal } from "components/modals";
import { updateReport, deleteReport } from "apis";

const LinearProgressWithLabel = () => {
  // TODO: Add progress computation
  return (
    <Box className={styles.progressContainer}>
      <Text className={styles.progressLabel}>{locale.preparing}</Text>
      <Box className={styles.progressInnerContainer} mr={1}>
        <LinearProgress />
      </Box>
    </Box>
  );
};

const checkFileTypeImage = (file, status) => {
  const fileExtension = file?.split(".").pop();
  const csvImage = status === ReportStatus?.Cancelled ? CSVCancelledIcon : CSVIcon;
  const fileImage = {
    csv: csvImage,
    // there is no excel icon asset yet, reuse the spreadsheet icon
    xlsx: csvImage,
    pdf: status === ReportStatus?.Cancelled ? PDFCancelledIcon : PDFIcon,
  };

  return fileImage[fileExtension];
};

const renderValue = (status, fileUrl) => {
  const component = {
    [`in progress`]: <LinearProgressWithLabel />,
    [`cancelled`]: <Text>{locale?.exportCanceled}</Text>,
    [`success`]: (
      <Button outline primary onClick={() => window.open(fileUrl)}>
        {locale?.downloadSingle}
      </Button>
    ),
  };

  return component[status];
};

const DownloadCard = ({ data, inDrawer, key, reloadData, isNew = false }) => {
  const { addAlert } = useAlert();
  const deleteModal = useModal();
  const { acknowledgeDownload, discardPendingDownload } = useContext(DownloadsContext);
  const reportUpdate = useApi({
    api: updateReport,
  });

  const removeReport = useApi({
    api: deleteReport,
  });

  const onDeleteReport = async (reportId) => {
    const resp = await removeReport.request({ reportId });
    if (resp) {
      deleteModal.close();
      acknowledge();
      addAlert({
        type: "success",
        title: locale.exportDeleted,
        subtitle: locale.theExportedFileHasBeenDeletedFromDownloads,
      });
      reloadData();
    }
  };

  const dataValue = reportUpdate?.result ?? data;

  const acknowledge = () => {
    if (isNew) acknowledgeDownload(dataValue?.reportId);
  };

  const onCardClick = (e) => {
    if (e.currentTarget.contains(e.target)) acknowledge();
  };

  return (
    <Card
      className={classNames(styles.cardContainer, { [styles.newDownload]: isNew })}
      key={key}
      onClick={dataValue?.status !== ReportStatus?.InProgress ? onCardClick : undefined}
    >
      <CardContent className={styles.cardImageContent}>
        <Image
          src={checkFileTypeImage(dataValue?.fileName, dataValue?.status)}
          className={styles.logo}
        />
      </CardContent>
      {inDrawer ? (
        <CardContent style={{ width: "70%", alignSelf: "center" }}>
          <Text>{dataValue?.fileName}</Text>
          {dataValue?.status === ReportStatus?.InProgress ? (
            <LinearProgressWithLabel progress={dataValue?.progress} />
          ) : (
            <Button
              outline
              primary
              className={styles.downloadButton}
              onClick={() => window.open(dataValue?.fileUrl)}
            >
              {locale?.downloadSingle}
            </Button>
          )}
          <Pill cement={true}>{prettifyReportType(dataValue?.reportType)}</Pill>
          <div style={{ display: "flex", gap: 10, alignSelf: "end" }}>
            <Text className={styles?.subBusinessId}>{formatBytes(dataValue?.fileSize, 2)}</Text>
            <Text className={styles?.subBusinessId}>{dateFormat(dataValue?.createdAt)}</Text>
          </div>
        </CardContent>
      ) : (
        <>
          <CardContent style={{ width: "50%", alignSelf: "center" }} className={styles.cardContent}>
            <Text
              className={styles.fileName}
              {...(dataValue?.status === ReportStatus?.Cancelled ? { strike: true } : {})}
            >
              {dataValue?.fileName}
            </Text>
            <div className={styles.meta}>
              <Pill cement={true}>{prettifyReportType(dataValue?.reportType)}</Pill>
              <Text className={styles.subBusinessId}>{formatBytes(dataValue?.fileSize, 2)}</Text>
              <Text className={styles.subBusinessId}>{dateFormat(dataValue?.createdAt)}</Text>
            </div>
          </CardContent>
          <CardContent
            style={{
              width: "50%",
              alignSelf: "center",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            {renderValue(dataValue?.status, dataValue?.fileUrl)}
          </CardContent>
          <CardContent style={{ alignSelf: "center" }}>
            {dataValue?.status === ReportStatus?.InProgress ? (
              <IconButton
                disabled={reportUpdate?.loading}
                onClick={() => {
                  discardPendingDownload(dataValue?.fileName);
                  reportUpdate?.request({
                    reportId: dataValue?.reportId,
                    status: ReportStatus?.Cancelled,
                  });
                }}
              >
                {reportUpdate?.loading ? (
                  <CircularProgress className={styles.circular} />
                ) : (
                  <ClearOutlined />
                )}
              </IconButton>
            ) : (
              <IconButton
                disabled={removeReport?.loading}
                onClick={(e) => {
                  e.stopPropagation();
                  deleteModal.show();
                }}
              >
                <DeleteOutlineOutlined className={styles.deleteIcon} />
              </IconButton>
            )}
          </CardContent>
        </>
      )}
      <SuccessModal
        {...deleteModal}
        image={DeleteReport}
        title={locale.deleteExport}
        titleStyle={styles.modalTitle}
        contentStyle={styles.modalContent}
        content={locale.deleteExportMessage}
        contentHighlight={[<b>{dataValue?.fileName}</b>]}
        loading={removeReport?.loading}
        primary={{ text: locale.delete, onClick: () => onDeleteReport(data?.reportId) }}
        centerButtons
        secondary={{ text: locale.cancel, outline: true, style: { color: "#5349b7" } }}
      />
    </Card>
  );
};

export default DownloadCard;
